// Constants
import { Colors } from "@ms/uno-constants/lib/local/Colors";
// Styles
import { concatStyleSetsWithProps, memoizeFunction, NeutralColors } from "@fluentui/react";
import { flexChildSizeFixed, flexParent, fontColorNeutralPrimary, fontColorNeutralSecondary, fontColorNeutralTertiary, themePrimaryColor } from "../../../utilities/Mixin.styles";
// Utilities
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { isLoopTheme, isWindowsHighContrast } from "../../../utilities/ThemingUtilities";
const checkboxSize = "16px";
/**
 * Helper to get styles function for Checkbox
 * @param theme Uno theme
 * @param customStyles Any custom style overrides
 */ export const getCheckboxStyleFunction = memoizeFunction((theme, customStyles)=>(props)=>{
        const { checked, disabled } = props;
        const isLoop = isLoopTheme(theme);
        // Don't apply these in HC, or else it will override Fluent's Checkbox HC styles
        const root = !isWindowsHighContrast() && {
            ":hover, :active, :focus": {
                "> .ms-Checkbox-label": {
                    "> .ms-Checkbox-checkbox": {
                        backgroundColor: checkboxActiveBackgroundColor(theme, checked, disabled),
                        borderColor: checkboxActiveBorderColor(theme, checked, disabled),
                        "> .ms-Checkbox-checkmark": {
                            color: checkmarkActiveColor(theme, checked)
                        }
                    },
                    "> .ms-Checkbox-text": disabled ? fontColorNeutralTertiary(theme) : fontColorNeutralPrimary(theme)
                }
            }
        };
        const checkbox = [
            flexChildSizeFixed(checkboxSize),
            {
                width: checkboxSize,
                height: checkboxSize,
                backgroundColor: checkboxBackgroundColor(theme, checked, disabled),
                borderColor: checkboxBorderColor(theme, checked, disabled),
                // This checkmark style is defined with selector to have higher specificity than Loop's default styling
                "> .ms-Checkbox-checkmark": flexParent()
            },
            isLoop && {
                borderRadius: "3px"
            }
        ];
        const checkmark = {
            color: checkmarkColor(theme, checked)
        };
        const text = disabled ? fontColorNeutralTertiary(theme) : fontColorNeutralSecondary(theme);
        const checkboxStyles = {
            root,
            checkbox,
            checkmark,
            text
        };
        return concatStyleSetsWithProps(props, checkboxStyles, customStyles);
    });
const checkboxBorderColor = (theme, checked = false, disabled = false)=>{
    if (disabled) {
        return Colors.BorderColorNeutralTertiaryAlt;
    }
    if (checked) {
        switch(theme.name){
            case PlannerTheme.HighContrast:
                return Colors.TeamsContrastHyperlinkColor;
            default:
                return themePrimaryColor(theme);
        }
    }
    switch(theme.name){
        case PlannerTheme.Dark:
            return NeutralColors.gray60;
        case PlannerTheme.HighContrast:
            return Colors.White;
        case PlannerTheme.Light:
        default:
            return Colors.NeutralSecondary;
    }
};
const checkboxActiveBorderColor = (theme, checked = false, disabled = false)=>{
    if (disabled) {
        return Colors.BorderColorNeutralTertiaryAlt;
    }
    if (checked) {
        switch(theme.name){
            case PlannerTheme.HighContrast:
                return Colors.White;
            case PlannerTheme.Light:
                return Colors.TeamsAppBrand04;
            case PlannerTheme.Dark:
                return Colors.TeamsAppBrand00;
            default:
                return Colors.ThemeSecondary;
        }
    }
    switch(theme.name){
        case PlannerTheme.Light:
            return Colors.TeamsAppBrand00;
        case PlannerTheme.Dark:
            return Colors.TeamsAppBrand08;
        case PlannerTheme.HighContrast:
            return Colors.White;
        default:
            return Colors.BorderColorNeutralPrimary;
    }
};
const checkboxBackgroundColor = (theme, checked = false, disabled = false)=>{
    return checked ? checkboxBorderColor(theme, checked, disabled) : "";
};
const checkboxActiveBackgroundColor = (theme, checked = false, disabled = false)=>{
    if (disabled) {
        return checkboxActiveBorderColor(theme, checked, disabled);
    }
    if (checked) {
        switch(theme.name){
            case PlannerTheme.HighContrast:
                return Colors.Black;
            default:
                return checkboxActiveBorderColor(theme, checked, disabled);
        }
    }
    return "";
};
const checkmarkColor = (theme, checked = false)=>{
    if (checked) {
        switch(theme.name){
            case PlannerTheme.Dark:
            case PlannerTheme.HighContrast:
                return Colors.Black;
            case PlannerTheme.Light:
            default:
                return Colors.White;
        }
    }
    // Not checked, checkmark is not displayed
    return "";
};
const checkmarkActiveColor = (theme, checked = false)=>{
    if (checked) {
        switch(theme.name){
            case PlannerTheme.Dark:
                return Colors.Black;
            case PlannerTheme.HighContrast:
                return Colors.White;
            default:
                return "";
        }
    }
    switch(theme.name){
        case PlannerTheme.Light:
            return Colors.TeamsAppBrand00;
        case PlannerTheme.Dark:
            return Colors.TeamsAppBrand08;
        case PlannerTheme.HighContrast:
            return Colors.White;
        default:
            return "";
    }
};
